import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import type { InvokeResponseBase } from '../../lib/types';

interface Deadline {
  template_id: string;
  label: string;
  category: string;
  periodicity: string;
  due_date: string;
  status: 'passed' | 'imminent' | 'upcoming' | 'distant';
  days_until: number;
}

interface EcheancierResult {
  fiscal_year: number;
  deadlines: Deadline[];
  next_critical: Deadline | null;
  counts_by_status: Record<'passed' | 'imminent' | 'upcoming' | 'distant', number>;
}

const STATUS_STYLE: Record<Deadline['status'], string> = {
  imminent: 'bg-red-100 text-red-900 border-red-300',
  upcoming: 'bg-amber-100 text-amber-900 border-amber-300',
  distant: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  passed: 'bg-zinc-200 text-zinc-500 border-zinc-300 line-through',
};

const STATUS_LABEL: Record<Deadline['status'], string> = {
  imminent: 'Imminente',
  upcoming: 'À venir',
  distant: 'Lointaine',
  passed: 'Passée',
};

const CATEGORY_ICON: Record<string, string> = {
  tva: '🧾',
  is: '🏛',
  ras: '✂️',
  cnss: '👥',
  tfp: '🎓',
  foprolos: '🏠',
  irpp: '👤',
};

export function EcheancierPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [todayInput, setTodayInput] = useState(today);
  const [regime, setRegime] = useState('reel');
  const [hasPersonnel, setHasPersonnel] = useState(true);
  const [paysHonoraires, setPaysHonoraires] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const res = await api.invokeAgent('echeancier', {
        payload: {
          fiscal_year: fiscalYear,
          today: todayInput,
          regime_fiscal: regime,
          has_personnel: hasPersonnel,
          pays_honoraires_or_loyers: paysHonoraires,
        },
        fiscal_reference_date: todayInput,
      });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Échéancier"
      module="Extension · Phase 1"
      model="Haiku 4.5"
      description={
        <>
          Calendrier fiscal multi-clients tunisien — TVA mensuelle, RAS, CNSS trimestrielle,
          acomptes IS, TFP, Foprolos. Matérialisation déterministe pour l'exercice donné +
          classement par urgence.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Profil de l'entreprise">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Année fiscale">
              <input
                type="number"
                className={inputCls}
                value={fiscalYear}
                onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))}
                min={2020}
                max={2100}
              />
            </Field>
            <Field label="Date du jour">
              <input
                type="date"
                className={inputCls}
                value={todayInput}
                onChange={(e) => setTodayInput(e.target.value)}
                required
              />
            </Field>
            <Field label="Régime fiscal">
              <select className={inputCls} value={regime} onChange={(e) => setRegime(e.target.value)}>
                <option value="reel">Réel</option>
                <option value="reel_simplifie">Réel simplifié</option>
                <option value="forfaitaire">Forfaitaire</option>
                <option value="agricole">Agricole</option>
              </select>
            </Field>
            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={hasPersonnel}
                  onChange={(e) => setHasPersonnel(e.target.checked)}
                />
                Personnel salarié
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={paysHonoraires}
                  onChange={(e) => setPaysHonoraires(e.target.checked)}
                />
                Verse honoraires / loyers (RAS applicable)
              </label>
            </div>
          </div>
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Invocation…' : 'Calculer le calendrier'}
        </button>
      </form>

      {error && (
        <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>
      )}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <EcheancierResultView result={result as EcheancierResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function EcheancierResultView({ result }: { result: EcheancierResult }) {
  const { counts_by_status: c } = result;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Counter label="Imminentes" value={c.imminent} cls="text-red-700" />
        <Counter label="À venir" value={c.upcoming} cls="text-amber-700" />
        <Counter label="Lointaines" value={c.distant} cls="text-zinc-700" />
        <Counter label="Passées" value={c.passed} cls="text-zinc-400" />
      </div>

      {result.next_critical && (
        <div className="p-3 rounded bg-cream/60 border border-border">
          <div className="text-xs uppercase tracking-wider text-taupe mb-1">Prochaine échéance critique</div>
          <div className="font-medium">
            {result.next_critical.label} —{' '}
            <span className="font-mono">{result.next_critical.due_date}</span> (J{result.next_critical.days_until >= 0 ? '-' : '+'}
            {Math.abs(result.next_critical.days_until)})
          </div>
        </div>
      )}

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-taupe border-b border-border">
              <th className="px-2 py-2">Cat.</th>
              <th className="px-2 py-2">Échéance</th>
              <th className="px-2 py-2">Statut</th>
              <th className="px-2 py-2">Date</th>
              <th className="px-2 py-2 text-right">Jours</th>
              <th className="px-2 py-2">Périodicité</th>
            </tr>
          </thead>
          <tbody>
            {result.deadlines.map((d, i) => (
              <tr key={i} className="border-b border-border/50">
                <td className="px-2 py-1.5">
                  <span title={d.category}>{CATEGORY_ICON[d.category] ?? '·'}</span>
                  <span className="ml-2 text-xs uppercase tracking-wider text-taupe">{d.category}</span>
                </td>
                <td className="px-2 py-1.5">{d.label}</td>
                <td className="px-2 py-1.5">
                  <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLE[d.status]}`}>
                    {STATUS_LABEL[d.status]}
                  </span>
                </td>
                <td className="px-2 py-1.5 font-mono">{d.due_date}</td>
                <td className="px-2 py-1.5 text-right font-mono">
                  {d.days_until >= 0 ? `J-${d.days_until}` : `J+${Math.abs(d.days_until)}`}
                </td>
                <td className="px-2 py-1.5 text-xs text-taupe">{d.periodicity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Counter({ label, value, cls }: { label: string; value: number; cls: string }) {
  return (
    <div className="card text-center !p-3">
      <div className={`font-serif text-3xl font-bold ${cls}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-taupe mt-1">{label}</div>
    </div>
  );
}
